import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Button, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Radio, RadioGroup} from '@chakra-ui/react'
import WebDrawer from '../web-drawer'

/**
 * Drawer for removing selected items from a collection.
 * Available in all collections, including My Favourites.
 *
 * Props:
 * - isOpen / onClose
 * - collectionName: string — name of the current collection (used in Opção 1 label)
 * - isViewingFolder: boolean — if false (My Favourites), only "remove from all" is shown
 * - onConfirm({mode}) — mode: 'folder' | 'all'
 */
const WishlistRemoveItemsDrawer = ({isOpen, onClose, collectionName, isViewingFolder, onConfirm, selectedCount = 1}) => {
    const {formatMessage} = useIntl()

    // 'folder' = remove from this collection only; 'all' = remove from entire wishlist
    const [mode, setMode] = useState('folder')

    // Reset selection when drawer opens
    useEffect(() => {
        if (isOpen) {
            setMode(isViewingFolder ? 'folder' : 'all')
        }
    }, [isOpen, isViewingFolder])

    const handleConfirm = () => {
        onConfirm?.({mode})
        onClose()
    }

    const title = formatMessage({
        id: 'wishlist.remove_items.title',
        defaultMessage: 'Remove Items'
    })

    return (
        <WebDrawer isOpen={isOpen} onClose={onClose} title={title} size="full">
            <Stack spacing={6}>
                {/* Heading */}
                <Text textStyle="bodySmRegular">
                    {selectedCount > 1 ? (
                        <>
                            <FormattedMessage
                                id="wishlist.remove_items.heading_multi_prefix"
                                defaultMessage="Choose what should happen to these"
                            />
                            {' '}
                            <Text as="span" textStyle="bodySmBold">
                                <FormattedMessage
                                    id="wishlist.remove_items.heading_multi_count"
                                    defaultMessage="{count} items"
                                    values={{count: selectedCount}}
                                />
                            </Text>
                            {':'}
                        </>
                    ) : (
                        <FormattedMessage
                            id="wishlist.remove_items.heading"
                            defaultMessage="Choose what should happen to this item:"
                        />
                    )}
                </Text>

                {/* Radio options */}
                <RadioGroup value={mode} onChange={setMode}>
                    <Stack spacing={4}>
                        {/* Option 1 — visible only when inside a custom folder */}
                        {isViewingFolder && (
                            <Radio value="folder">
                                <Stack spacing={1} ml={2}>
                                    <Text textStyle="bodySmBold">
                                        <FormattedMessage
                                            id="wishlist.remove_items.option_folder_label_prefix"
                                            defaultMessage="Remove from"
                                        />
                                        {' \u201C'}{collectionName}{'\u201D '}
                                        <FormattedMessage
                                            id="wishlist.remove_items.option_folder_label_suffix"
                                            defaultMessage="Only"
                                        />
                                    </Text>
                                    <Text textStyle="bodySmRegular" color="webTypo.textSecondary">
                                        <FormattedMessage
                                            id="wishlist.remove_items.option_folder_desc_before"
                                            defaultMessage="The item will stay in"
                                        />
                                        {' '}
                                        <Text as="span" textStyle="bodySmBold" color="webTypo.textSecondary">
                                            &ldquo;<FormattedMessage
                                                id="wishlist.folder.my_favourites"
                                                defaultMessage="My Favourites"
                                            />&rdquo;
                                        </Text>
                                    </Text>
                                </Stack>
                            </Radio>
                        )}

                        {/* Option 2 — always visible */}
                        <Radio value="all">
                            <Stack spacing={1} ml={2}>
                                <Text textStyle="bodySmBold">
                                    <FormattedMessage
                                        id="wishlist.remove_items.option_all_label"
                                        defaultMessage="Remove from all collections"
                                    />
                                </Text>
                                <Text textStyle="bodySmRegular" color="webTypo.textSecondary">
                                    <FormattedMessage
                                        id="wishlist.remove_items.option_all_desc"
                                        defaultMessage="The item will be permanently removed from your wishlist"
                                    />
                                </Text>
                            </Stack>
                        </Radio>
                    </Stack>
                </RadioGroup>

                {/* Action buttons */}
                <Stack spacing={3} pt={2}>
                    <Button type="button" variant="webPrimary" w="100%" onClick={handleConfirm}>
                        <FormattedMessage id="wishlist.remove_items.confirm_btn" defaultMessage="Confirm" />
                    </Button>
                    <Button type="button" variant="webSecondaryOutline" w="100%" onClick={onClose}>
                        <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                    </Button>
                </Stack>
            </Stack>
        </WebDrawer>
    )
}

WishlistRemoveItemsDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    collectionName: PropTypes.string,
    isViewingFolder: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    selectedCount: PropTypes.number
}

export default WishlistRemoveItemsDrawer
